import { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Switch from '../../../../components/Switch';
import TooltipWrapper from '../../../../components/TooltipWrapper';
import { styles } from '../../../../styles/themes/style';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { useAppActions } from '../../../../stores/application/useAppActions';
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers';
import { useSignalsRequest } from '../../../../services/apis/signals/useSignalsRequest';
import {
  ISignalAccount, ISignalsAccess, ISignalSource, LinkedInReadKind, SIGNAL_SOURCE_INFO, SignalSourceType,
} from '../../../../interfaces/models/signals';
import {
  Card, CardHead, CardTitle, GhostButton, Icon, Muted, Pill, PrimaryButton, SignalIconName, TextInput, fromNow, plural,
} from './signalsUi';

type AccountSourceType = Exclude<SignalSourceType, 'post'>;

const ACCOUNT_SOURCES: { type: AccountSourceType; icon: SignalIconName }[] = [
  { type: 'profile_views', icon: 'eye' },
  { type: 'own_posts', icon: 'post' },
  { type: 'job_changes', icon: 'briefcase' },
];

/** Reads shown per account — the kinds the radar and campaigns share. */
const READ_KINDS: { kind: LinkedInReadKind; label: string }[] = [
  { kind: 'profile', label: 'profiles' },
  { kind: 'post', label: 'posts' },
];

interface ISignalSourcesPanelProps {
  access: ISignalsAccess;
  accounts: ISignalAccount[];
  dailyReadCaps: Record<LinkedInReadKind, number> | null;
  sources: ISignalSource[];
  loading: boolean;
  /** After any change: reload sources and plan access. */
  onChanged: () => void;
}

/** One line under a source: when it last ran, what it found, or why it stopped. */
const SourceStatus: FunctionComponent<{ source?: ISignalSource }> = ({ source }) => {
  if (!source || !source.enabled) return null;
  if (source.lastError) {
    return (
      <StatusLine $error>
        <Icon name="alert" size={13} />
        {source.lastError}
      </StatusLine>
    );
  }
  if (!source.lastSuccessAt) return <StatusLine>Waiting for the first check — usually within the hour, during working hours.</StatusLine>;
  return (
    <StatusLine>
      Checked {fromNow(source.lastSuccessAt)} · {plural(source.stats?.signals ?? 0, 'signal')} found so far
    </StatusLine>
  );
};

/**
 * What each cloud-connected LinkedIn account watches: its profile viewers, its own
 * posts, its network's job changes, and posts someone pasted. Reading happens in the
 * background a few times a day, within the account's LinkedIn read budget.
 */
const SignalSourcesPanel: FunctionComponent<ISignalSourcesPanelProps> = ({
  access, accounts, dailyReadCaps, sources, loading, onChanged,
}) => {
  const api = useSignalsRequest();
  const navigate = useNavigate();
  const { setToast } = useAppActions();
  const { setDestructiveModal, hideModal } = useAppHandlers();

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [postUrls, setPostUrls] = useState<Record<string, string>>({});

  const sourceFor = (accountId: string, type: AccountSourceType) =>
    sources.find(s => s.accountId === accountId && s.type === type);

  const postsFor = (accountId: string) => sources.filter(s => s.accountId === accountId && s.type === 'post');

  const trialFull = access.isTrial && !access.canAddSource;

  const toggleAccountSource = async (accountId: string, type: AccountSourceType, enabled: boolean) => {
    setBusyKey(`${accountId}:${type}`);
    const res = await api.setAccountSource(accountId, type, enabled);
    setBusyKey(null);
    if (!res?.success) setToast({ type: 'error', message: res?.message || 'Could not change this source.' });
    onChanged();
  };

  const addPost = async (accountId: string) => {
    const url = (postUrls[accountId] || '').trim();
    if (!url) return;
    setBusyKey(`${accountId}:add-post`);
    const res = await api.addPostSource(accountId, url);
    setBusyKey(null);
    if (!res?.success) {
      setToast({ type: 'error', message: res?.message || 'Could not watch this post.' });
      return;
    }
    setPostUrls(prev => ({ ...prev, [accountId]: '' }));
    setToast({
      type: 'success',
      message: res.data?.alreadyWatched ? 'This post is already being watched.' : 'Post added. The first check runs within the hour, during working hours.',
    });
    onChanged();
  };

  const togglePost = async (source: ISignalSource, enabled: boolean) => {
    setBusyKey(source._id);
    const res = await api.updateSource(source._id, enabled);
    setBusyKey(null);
    if (!res?.success) setToast({ type: 'error', message: res?.message || 'Could not change this post.' });
    onChanged();
  };

  const removePost = (source: ISignalSource) => {
    setDestructiveModal({
      headingText: 'Stop watching this post?',
      descriptionText: 'People already found on it stay in your Signals list.',
      confirmBtnTitle: 'Stop watching',
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        const res = await api.deleteSource(source._id);
        if (!res?.success) setToast({ type: 'error', message: res?.message || 'Could not remove this post.' });
        onChanged();
      },
    });
  };

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>What the radar watches</CardTitle>
          <Muted>Every LinkedIn account connected to the workspace watches its own sources, reading only what it can see itself, within LinkedIn’s safe daily limits.</Muted>
        </div>
        {access.isTrial && access.limit !== null && (
          <TooltipWrapper tooltipText="Upgrade to Growth to watch more sources at once.">
            <Pill $bg={trialFull ? styles.colors.yellow200 : styles.colors.primary100} $fg={trialFull ? '#c48a00' : styles.colors.primary600}>
              Trial · {access.used} of {access.limit} sources on
            </Pill>
          </TooltipWrapper>
        )}
      </CardHead>

      {!loading && accounts.length === 0 && (
        <EmptyAccounts>
          <Icon name="radar" size={28} />
          <div>
            <strong>Connect a LinkedIn account in the cloud to start.</strong>
            <Muted>Signals read through the same LinkedIn accounts your campaigns use, so nothing depends on a browser being open.</Muted>
          </div>
          <PrimaryButton type="button" onClick={() => navigate(AppRoutes.SETTINGS_SENDING_ACCOUNTS)}>
            Go to Sending accounts
          </PrimaryButton>
        </EmptyAccounts>
      )}

      {accounts.map(account => {
        const posts = postsFor(account.accountId);
        return (
          <AccountBlock key={account.accountId}>
            <AccountHead>
              <div>
                <AccountName>
                  {account.name || account.email || 'LinkedIn account'}
                  {account.isMine
                    ? <Pill $bg={styles.colors.black100} $fg={styles.colors.black500}>You</Pill>
                    : account.ownerName && (
                      <TooltipWrapper tooltipText="People this account finds become this teammate’s leads.">
                        <Pill $bg={styles.colors.black100} $fg={styles.colors.black500}>{account.ownerName}</Pill>
                      </TooltipWrapper>
                    )}
                </AccountName>
                {(account.email || account.usedBy.length > 0) && (
                  <AccountMeta>
                    {[
                      account.name && account.email ? account.email : '',
                      account.usedBy.length ? `Used by ${account.usedBy.slice(0, 3).join(', ')}${account.usedBy.length > 3 ? ` +${account.usedBy.length - 3}` : ''}` : '',
                    ].filter(Boolean).join(' · ')}
                  </AccountMeta>
                )}
              </div>
              {dailyReadCaps && (
                <TooltipWrapper tooltipText="LinkedIn reads made through this account today by campaigns and Signals together. The radar stops before campaigns could run short.">
                  <Reads>
                    Reads today: {READ_KINDS.map(({ kind, label }) => `${account.readsToday?.[kind] ?? 0}/${dailyReadCaps[kind]} ${label}`).join(' · ')}
                  </Reads>
                </TooltipWrapper>
              )}
            </AccountHead>

            {ACCOUNT_SOURCES.map(({ type, icon }) => {
              const source = sourceFor(account.accountId, type);
              const on = !!source?.enabled;
              const blocked = !on && trialFull;
              return (
                <SourceRow key={type}>
                  <SourceIcon $on={on}><Icon name={icon} size={16} /></SourceIcon>
                  <SourceText>
                    <SourceTitle>{SIGNAL_SOURCE_INFO[type].title}</SourceTitle>
                    <Muted>{SIGNAL_SOURCE_INFO[type].description}</Muted>
                    <SourceStatus source={source} />
                  </SourceText>
                  <Switch
                    prefilledValue={on}
                    $disabled={busyKey === `${account.accountId}:${type}` || blocked}
                    tooltipMessage={blocked ? (access.message || 'Your trial is watching as many sources as it can.') : ''}
                    onClickHandler={(value: boolean) => toggleAccountSource(account.accountId, type, value)}
                  />
                </SourceRow>
              );
            })}

            <PostsBlock>
              <SourceTitle>Watched posts</SourceTitle>
              <Muted>Paste a competitor’s, partner’s or industry voice’s post. Everyone who comments or reacts shows up here for 30 days.</Muted>
              {posts.map(post => (
                <PostRow key={post._id}>
                  <Icon name="post" size={15} />
                  <PostText>
                    <PostLabel href={post.postUrl || undefined} target="_blank" rel="noreferrer">
                      {post.label}
                    </PostLabel>
                    <SourceStatus source={post} />
                  </PostText>
                  <Switch
                    prefilledValue={post.enabled}
                    $disabled={busyKey === post._id || (!post.enabled && trialFull)}
                    onClickHandler={(value: boolean) => togglePost(post, value)}
                  />
                  <IconButton type="button" onClick={() => removePost(post)} aria-label="Stop watching this post">
                    <Icon name="trash" size={15} />
                  </IconButton>
                </PostRow>
              ))}
              <AddPostRow
                onSubmit={e => { e.preventDefault(); void addPost(account.accountId); }}
              >
                <TextInput
                  type="text"
                  inputMode="url"
                  placeholder="https://www.linkedin.com/posts/…"
                  value={postUrls[account.accountId] || ''}
                  onChange={e => setPostUrls(prev => ({ ...prev, [account.accountId]: e.target.value }))}
                  name={`signals-post-${account.accountId}`}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
                <GhostButton
                  type="submit"
                  disabled={!postUrls[account.accountId]?.trim() || busyKey === `${account.accountId}:add-post` || trialFull}
                >
                  <Icon name="plus" size={14} /> Watch post
                </GhostButton>
              </AddPostRow>
            </PostsBlock>
          </AccountBlock>
        );
      })}
    </Card>
  );
};

export default SignalSourcesPanel;

// ─── Styles ───────────────────────────────────────────────────────────────────

const EmptyAccounts = styled.div`
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  padding: 18px; border: 1px dashed ${styles.colors.black200}; border-radius: 14px;
  color: ${styles.colors.black400};
  font-family: NunitoSans; font-size: 14px;
  strong { display: block; color: ${styles.colors.black600}; margin-bottom: 2px; }
  > div { flex: 1; min-width: 220px; }
`;

const AccountBlock = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  border-top: 1px solid #eef1f5; padding-top: 14px;
  &:first-of-type { border-top: none; padding-top: 0; }
`;

const AccountHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  margin-bottom: 4px;
`;

const AccountName = styled.span`
  display: inline-flex; align-items: center; gap: 8px;
  font-family: NunitoSans; font-size: 14px; font-weight: 800; color: ${styles.colors.black600};
`;

const AccountMeta = styled.span`
  display: block; margin-top: 2px;
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black300};
`;

const Reads = styled.span`
  font-family: NunitoSans; font-size: 12px; color: ${styles.colors.black400};
`;

const SourceRow = styled.div`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 10px 0;
`;

const SourceIcon = styled.span<{ $on: boolean }>`
  width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: ${({ $on }) => ($on ? styles.colors.primary100 : styles.colors.black100)};
  color: ${({ $on }) => ($on ? styles.colors.primary600 : styles.colors.black400)};
`;

const SourceText = styled.div`
  flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px;
`;

const SourceTitle = styled.span`
  font-family: NunitoSans; font-size: 14px; font-weight: 700; color: ${styles.colors.black600};
`;

const StatusLine = styled.span<{ $error?: boolean }>`
  display: inline-flex; align-items: center; gap: 5px;
  font-family: NunitoSans; font-size: 12px; line-height: 16px;
  color: ${({ $error }) => ($error ? styles.colors.red600 : styles.colors.black300)};
`;

const PostsBlock = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  margin: 4px 0 0 44px;
`;

const PostRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 6px 0;
  color: ${styles.colors.black400};
`;

const PostText = styled.div`
  flex: 1; min-width: 0; display: flex; flex-direction: column;
`;

const PostLabel = styled.a`
  font-family: NunitoSans; font-size: 13px; font-weight: 600; color: ${styles.colors.black600};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  text-decoration: none;
  &:hover { color: ${styles.colors.primary600}; }
`;

const IconButton = styled.button`
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  border: none; background: transparent; cursor: pointer; color: ${styles.colors.black300};
  &:hover { background: ${styles.colors.red200}; color: ${styles.colors.red600}; }
`;

const AddPostRow = styled.form`
  display: flex; gap: 8px; margin-top: 4px;
  > input { flex: 1; }
`;
